import { useEffect, useMemo, useState } from "react";
import {
  Bot,
  BookOpen,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Search,
  Sparkles,
  Terminal,
  WandSparkles,
} from "lucide-react";
import { toast } from "sonner";
import { fetchServerGuide, searchServerGuide } from "@/lib/api";

function CommandCard({ item, color }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const command = item.command || item.usage || item.title || "";

  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      toast.success("Command copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy command");
    }
  };

  return (
    <div
      className="rounded-2xl glass overflow-hidden border border-white/10 hover:border-white/20 transition-all"
      data-testid={`drkceco-command-${item.id}`}
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: `${color}18`,
              color,
            }}
          >
            <Terminal size={17} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <code
                className="font-mono text-sm sm:text-base font-bold break-all"
                style={{ color }}
              >
                {item.command || item.title}
              </code>

              {item.category && (
                <span className="text-[9px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 text-white/40">
                  {item.category}
                </span>
              )}
            </div>

            {item.description && (
              <p className="text-sm text-white/55 mt-2 leading-relaxed">
                {item.description}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={copyCommand}
            className="shrink-0 p-2 rounded-lg bg-white/5 hover:bg-white/10"
            title="Copy command"
          >
            {copied ? (
              <Check size={15} className="text-emerald-400" />
            ) : (
              <Copy size={15} className="text-white/60" />
            )}
          </button>
        </div>

        {(item.usage || item.permission || item.examples?.length > 0) && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-4 text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white/70 flex items-center gap-1"
          >
            {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {open ? "Hide details" : "View details"}
          </button>
        )}

        {open && (
          <div className="mt-4 pt-4 border-t border-white/10 space-y-4">
            {item.usage && (
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/35 mb-2">
                  Usage
                </div>
                <code className="block rounded-lg bg-black/30 px-3 py-2 text-xs text-white/75 font-mono break-all">
                  {item.usage}
                </code>
              </div>
            )}

            {item.permission && (
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/35 mb-2">
                  Permission
                </div>
                <code className="text-xs text-white/60 font-mono">
                  {item.permission}
                </code>
              </div>
            )}

            {item.examples?.length > 0 && (
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/35 mb-2">
                  Examples
                </div>
                <div className="space-y-2">
                  {item.examples.map((example, index) => (
                    <code
                      key={`${item.id}-example-${index}`}
                      className="block rounded-lg bg-black/30 px-3 py-2 text-xs text-white/65 font-mono break-all"
                    >
                      {example}
                    </code>
                  ))}
                </div>
              </div>
            )}

            {item.how_to_use && (
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/35 mb-2">
                  How to use
                </div>
                <p className="text-xs text-white/55 whitespace-pre-wrap">
                  {item.how_to_use}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ServerDrkCEco({ serverKey, color = "#9387FE" }) {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [category, setCategory] = useState("All");

  useEffect(() => {
    if (!serverKey) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        const data = await fetchServerGuide(serverKey);

        if (!cancelled) {
          setGuide(data);
          setResults(data?.commands || []);
        }
      } catch (error) {
        console.error("Failed to load DrkC Eco guide:", error);

        if (!cancelled) {
          setGuide(null);
          setResults([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [serverKey]);

  const commands = useMemo(
    () => guide?.commands || [],
    [guide]
  );

  const categories = useMemo(() => {
    const values = commands
      .map((item) => item.category)
      .filter(Boolean);

    return ["All", ...Array.from(new Set(values))];
  }, [commands]);

  const visibleCommands = useMemo(() => {
    const source = query.trim() ? results : commands;

    if (category === "All") return source;

    return source.filter((item) => item.category === category);
  }, [commands, results, query, category]);

  const runSearch = async (value) => {
    setQuery(value);

    if (!value.trim()) {
      setResults(commands);
      setSearching(false);
      return;
    }

    setSearching(true);

    try {
      const data = await searchServerGuide(serverKey, value);
      setResults(data?.results || []);
    } catch (error) {
      console.error("DrkC Eco search failed:", error);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
        <div className="rounded-2xl glass p-8 animate-pulse">
          <div className="h-4 w-40 bg-white/10 rounded mb-4" />
          <div className="h-10 w-full bg-white/5 rounded-xl" />
          <div className="grid md:grid-cols-2 gap-4 mt-5">
            <div className="h-32 bg-white/5 rounded-2xl" />
            <div className="h-32 bg-white/5 rounded-2xl" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 mt-14"
      data-testid="server-drkceco"
    >
      {/* DrkC Eco header */}
      <div className="rounded-3xl overflow-hidden border border-white/10 bg-white/[0.025]">
        <div className="p-5 sm:p-7">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5">
            <div>
              <div
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold"
                style={{ color }}
              >
                <Sparkles size={13} />
                DrkC Eco
              </div>

              <h2 className="text-3xl sm:text-4xl font-black mt-2">
                Your Server Guide
              </h2>

              <p className="text-sm text-white/45 mt-2">
                Commands, guides and server knowledge for this server.
              </p>
            </div>

            <div className="flex items-center gap-2 text-xs text-white/35">
              <BookOpen size={14} style={{ color }} />
              {commands.length} documented command
              {commands.length === 1 ? "" : "s"}
            </div>
          </div>

          {/* Ask DrkC Eco */}
          <div className="mt-7">
            <div
              className="flex items-center gap-2 text-xs font-semibold mb-2"
              style={{ color }}
            >
              <Bot size={15} />
              Ask DrkC Eco
            </div>

            <div className="relative">
              <Search
                size={17}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30"
              />

              <input
                value={query}
                onChange={(e) => runSearch(e.target.value)}
                placeholder="Ask DrkC Eco anything about this server..."
                className="w-full h-12 rounded-xl bg-black/25 border border-white/10 pl-11 pr-12 text-sm outline-none focus:border-white/25 placeholder:text-white/25"
              />

              {searching && (
                <div
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white/10 animate-spin"
                  style={{ borderTopColor: color }}
                />
              )}
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              {[
                "How do I earn DC Coins?",
                "How do teams work?",
                "What commands can I use?",
              ].map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => runSearch(suggestion)}
                  className="text-[10px] px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/45 hover:text-white/75 hover:bg-white/10 transition"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Command showcase */}
        <div className="border-t border-white/10 p-5 sm:p-7">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div
                className="text-[10px] uppercase tracking-[0.25em] font-bold"
                style={{ color }}
              >
                Command Showcase
              </div>

              <h3 className="text-xl sm:text-2xl font-black mt-1">
                Server Commands
              </h3>
            </div>

            {categories.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {categories.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setCategory(item)}
                    className={`text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition ${
                      category === item
                        ? "text-white"
                        : "border-white/10 text-white/40 hover:text-white/70"
                    }`}
                    style={
                      category === item
                        ? {
                            background: `${color}20`,
                            borderColor: `${color}55`,
                            color,
                          }
                        : undefined
                    }
                  >
                    {item}
                  </button>
                ))}
              </div>
            )}
          </div>

          {visibleCommands.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              {visibleCommands.map((item) => (
                <CommandCard
                  key={item.id}
                  item={item}
                  color={color}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-10 text-center mt-6">
              <div
                className="w-12 h-12 rounded-2xl mx-auto flex items-center justify-center"
                style={{
                  background: `${color}12`,
                  color,
                }}
              >
                {query ? (
                  <Search size={20} />
                ) : (
                  <WandSparkles size={20} />
                )}
              </div>

              <h4 className="font-bold mt-4">
                {query
                  ? "No documented commands found"
                  : "Commands are being documented"}
              </h4>

              <p className="text-xs text-white/35 mt-2 max-w-md mx-auto">
                {query
                  ? "DrkC Eco only shows verified server knowledge. Try another search or ask about something else."
                  : "This server does not have any verified commands in DrkC Eco yet."}
              </p>
            </div>
          )}
        </div>

        <div className="px-5 sm:px-7 py-3 border-t border-white/10 text-[9px] uppercase tracking-[0.2em] text-white/25 flex items-center gap-2">
          <Bot size={11} />
          Powered by DrkC Eco · LostDC AI
        </div>
      </div>
    </section>
  );
}
